import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import NeonButton from '../../components/Common/Button';
import NeonInput from '../../components/Common/Input';
import { useAuth } from '../../context/AuthContext';
import { apiService } from '../../services/api';
import './RegisterPage.css';
import { useLanguage } from '../../context/LanguageContext';
import LegalConsent from '../../components/Legal/LegalConsent';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useLanguage();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    dateOfBirth: '',
    gender: '',
    placeOfStudy: '',
    role: 'student',
    inviteCode: '',
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });

    if (errors[e.target.name] || errors.general) {
      setErrors({});
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = t('fullNameRequired');

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      newErrors.email = t('validEmail');
    }

    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = t('dobRequired');
    }

    if (!formData.gender) {
      newErrors.gender = t('genderRequired');
    }

    if (formData.password.length < 6) {
      newErrors.password = t('passwordLength');
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t('passwordMismatch');
    }

    if (formData.role === 'doctor' && !formData.inviteCode.trim()) {
      newErrors.general = t('inviteRequired');
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);

    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        date_of_birth: formData.dateOfBirth,
        gender: formData.gender,
        role: formData.role,
      };

      if (formData.phone?.trim()) {
        payload.phone = formData.phone;
      }

      if (formData.placeOfStudy?.trim()) {
        payload.place_of_study = formData.placeOfStudy;
      }

      if (formData.role === 'doctor') {
        payload.invite_code = formData.inviteCode;
      }

      const response = await apiService.register(payload);

      if (response.success && response.data) {
        const user = {
          id: response.data.user_id || response.data.userId,
          name: response.data.name,
          email: response.data.email,
          phone: response.data.phone,
          date_of_birth:
            response.data.date_of_birth || response.data.dateOfBirth,
          gender: response.data.gender || formData.gender,
          place_of_study:
            response.data.place_of_study || response.data.placeOfStudy,
          role: response.data.role || 'student',
          subscription_plan: response.data.subscription_plan || (response.data.role === 'doctor' ? 'instructor' : 'starter')
        };

        login(user, response.data.token, true);
        navigate(user.role === 'doctor' ? '/doctor-review' : '/dashboard');
      } else {
        setErrors({
          general: response.message || t('registrationFailed')
        });
      }
    } catch (err) {
      setErrors({
        general: err.message || t('registrationFailed')
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="signup-container">
      <div className="signup-card">
        <div className="signup-header">
          <h1>{t('createYourAccount')}</h1>
          <p>{t('registerSubtitle')}</p>
        </div>

        <form onSubmit={handleSubmit}>
          {errors.general && (
            <div className="alert alert-error">
              <span className="alert-icon">⚠️</span>
              {errors.general}
            </div>
          )}

          <div className="form-grid">
            <NeonInput
              label={t('fullName')}
              name="name"
              value={formData.name}
              onChange={handleChange}
              error={errors.name}
              placeholder="Dr. Alex Morgan"
              disabled={loading}
            />

            <NeonInput
              label={t('emailAddress')}
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              error={errors.email}
              placeholder="doctor@example.com"
              disabled={loading}
            />

            <NeonInput
              label={t('phoneNumber')}
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              error={errors.phone}
              placeholder="+1 (555) 123-4567"
              disabled={loading}
            />

            <NeonInput
              label={t('dateOfBirth')}
              type="date"
              name="dateOfBirth"
              value={formData.dateOfBirth}
              onChange={handleChange}
              error={errors.dateOfBirth}
              disabled={loading}
              max={new Date().toISOString().split('T')[0]}
            />

            <div className="account-type-field">
              <label htmlFor="gender">{t('studentGender')}</label>
              <select id="gender" name="gender" value={formData.gender} onChange={handleChange} disabled={loading}>
                <option value="">{t('selectGender')}</option>
                <option value="female">{t('female')}</option>
                <option value="male">{t('male')}</option>
                <option value="unspecified">{t('preferNotSay')}</option>
              </select>
              {errors.gender && <small className="field-error">{errors.gender}</small>}
            </div>

            <NeonInput
              label={t('password')}
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              error={errors.password}
              placeholder="••••••••"
              disabled={loading}
            />

            <NeonInput
              label={t('confirmPassword')}
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              error={errors.confirmPassword}
              placeholder="••••••••"
              disabled={loading}
            />

            <div className="full-width">
              <NeonInput
                label={t('institution')}
                name="placeOfStudy"
                value={formData.placeOfStudy}
                onChange={handleChange}
                error={errors.placeOfStudy}
                placeholder="Harvard Medical School"
                disabled={loading}
              />
            </div>

            <div className="full-width account-type-field">
              <label htmlFor="role">{t('accountType')}</label>
              <select id="role" name="role" value={formData.role} onChange={handleChange} disabled={loading}>
                <option value="student">{t('student')}</option>
                <option value="doctor">{t('doctor')}</option>
              </select>
              <small>{t('doctorCodeHelp')}</small>
            </div>

            {formData.role === 'doctor' && (
              <div className="full-width">
                <NeonInput
                  label={t('doctorInvite')}
                  type="password"
                  name="inviteCode"
                  value={formData.inviteCode}
                  onChange={handleChange}
                  placeholder={t('enterInvite')}
                  disabled={loading}
                />
              </div>
            )}

          </div>

          <div className="button-container">
            <NeonButton
              type="submit"
              variant="primary"
              disabled={loading}
              className="signup-button"
            >
              {loading ? t('creatingAccount') : t('createAccount')}
            </NeonButton>

            <div className="signup-footer">
              <p>
                {t('alreadyAccount')}{' '}
                <Link to="/login">{t('signInHere')}</Link>
              </p>
              <LegalConsent variant="register" />
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegisterPage;
